// src/components/settings/EmailTemplates.js
import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';

const EmailTemplates = () => {
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState({
        subject: '',
        body_html: '',
        is_active: true
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await settingsService.getEmailTemplates();
            const data = response.data || response;
            setTemplates(data.templates || []);
        } catch (error) {
            showMessage('error', 'Failed to load email templates');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectTemplate = async (templateId) => {
        try {
            const response = await settingsService.getEmailTemplate(templateId);
            const data = response.data || response;
            setSelectedTemplate(data.template);
            setFormData({
                subject: data.template.subject,
                body_html: data.template.body_html,
                is_active: data.template.is_active
            });
            setEditing(false);
        } catch (error) {
            showMessage('error', 'Failed to load template details');
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedTemplate) return;

        setSaving(true);
        try {
            await settingsService.updateEmailTemplate(selectedTemplate.id, formData);
            showMessage('success', 'Email template updated successfully');
            setEditing(false);
            await fetchTemplates();
        } catch (error) {
            showMessage('error', error.message || 'Failed to update template');
        } finally {
            setSaving(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-large"></div>
                <p>Loading email templates...</p>
            </div>
        );
    }

    return (
        <div className="email-templates">
            <h2 className="section-title">
                <i className="fas fa-file-alt"></i>
                Email Template Management
            </h2>

            {message.text && (
                <div className={`message ${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            <div className="templates-layout">
                {/* Template List */}
                <div className="template-list">
                    <h3 className="list-title">Available Templates</h3>
                    {templates.map(template => (
                        <div
                            key={template.id}
                            className={`template-item ${selectedTemplate?.id === template.id ? 'active' : ''}`}
                            onClick={() => handleSelectTemplate(template.id)}
                        >
                            <div className="template-info">
                                <h4>{template.name}</h4>
                                <p>{template.subject}</p>
                            </div>
                            <div className="template-status">
                                <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                                    {template.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Template Editor */}
                <div className="template-editor">
                    {selectedTemplate ? (
                        <>
                            <div className="editor-header">
                                <div>
                                    <h3>{selectedTemplate.name}</h3>
                                    <p className="template-description">
                                        Last updated: {new Date(selectedTemplate.updated_at).toLocaleDateString()}
                                    </p>
                                </div>
                                {!editing && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setEditing(true)}
                                    >
                                        <i className="fas fa-edit"></i>
                                        Edit Template
                                    </button>
                                )}
                            </div>

                            {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                                <div className="variables-info">
                                    <h4><i className="fas fa-info-circle"></i> Available Variables</h4>
                                    <div className="variables-list">
                                        {selectedTemplate.variables.map((variable, index) => (
                                            <code key={index}>{`{{${variable}}}`}</code>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="template-form">
                                <div className="form-group">
                                    <label htmlFor="subject">Subject Line</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        disabled={!editing}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="body_html">Email Body (HTML)</label>
                                    <textarea
                                        id="body_html"
                                        name="body_html"
                                        value={formData.body_html}
                                        onChange={handleChange}
                                        disabled={!editing}
                                        rows="15"
                                        required
                                    ></textarea>
                                    <small>You can use HTML tags and the variables listed above</small>
                                </div>

                                <div className="form-group-checkbox">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleChange}
                                            disabled={!editing}
                                        />
                                        <span>Template is active</span>
                                    </label>
                                </div>

                                {editing && (
                                    <div className="form-actions">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setEditing(false);
                                                setFormData({
                                                    subject: selectedTemplate.subject,
                                                    body_html: selectedTemplate.body_html,
                                                    is_active: selectedTemplate.is_active
                                                });
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <div className="spinner"></div>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save"></i>
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </>
                    ) : (
                        <div className="no-selection">
                            <i className="fas fa-file-alt"></i>
                            <p>Select a template from the list to view and edit</p>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .email-templates {
                    animation: fadeIn 0.3s ease-in;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .loading-container {
                    text-align: center;
                    padding: 4rem 0;
                }

                .spinner-large {
                    width: 50px;
                    height: 50px;
                    border: 4px solid #f3f4f6;
                    border-top: 4px solid #667eea;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 1rem;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .section-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 2rem;
                    color: #2d3748;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .section-title i {
                    color: #667eea;
                }

                .message {
                    padding: 1rem 1.25rem;
                    border-radius: 10px;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-weight: 500;
                    animation: slideDown 0.3s ease-out;
                }

                .message.success {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                    border-left: 4px solid #28a745;
                }

                .message.error {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                    border-left: 4px solid #dc3545;
                }

                .templates-layout {
                    display: grid;
                    grid-template-columns: 350px 1fr;
                    gap: 2rem;
                    min-height: 600px;
                }

                .template-list {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 1.5rem;
                    height: fit-content;
                    max-height: 700px;
                    overflow-y: auto;
                }

                .list-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin: 0 0 1rem 0;
                    color: #2d3748;
                }

                .template-item {
                    background: white;
                    border: 2px solid #e9ecef;
                    border-radius: 10px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .template-item:hover {
                    border-color: #667eea;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
                }

                .template-item.active {
                    border-color: #667eea;
                    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%);
                }

                .template-info h4 {
                    margin: 0 0 0.5rem 0;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #2d3748;
                }

                .template-info p {
                    margin: 0;
                    font-size: 0.875rem;
                    color: #6c757d;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .template-status {
                    margin-top: 0.75rem;
                }

                .status-badge {
                    display: inline-block;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .status-badge.active {
                    background: #d4edda;
                    color: #155724;
                }

                .status-badge.inactive {
                    background: #f8d7da;
                    color: #721c24;
                }

                .template-editor {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 2rem;
                }

                .editor-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #dee2e6;
                }

                .editor-header h3 {
                    margin: 0 0 0.5rem 0;
                    font-size: 1.3rem;
                    font-weight: 600;
                    color: #2d3748;
                }

                .template-description {
                    margin: 0;
                    font-size: 0.875rem;
                    color: #6c757d;
                }

                .variables-info {
                    background: #e3f2fd;
                    border-left: 4px solid #2196f3;
                    padding: 1rem;
                    border-radius: 8px;
                    margin-bottom: 1.5rem;
                }

                .variables-info h4 {
                    margin: 0 0 0.75rem 0;
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #1976d2;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .variables-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .variables-list code {
                    background: white;
                    padding: 0.25rem 0.75rem;
                    border-radius: 4px;
                    font-size: 0.875rem;
                    color: #d63384;
                    border: 1px solid #dee2e6;
                }

                .template-form .form-group {
                    margin-bottom: 1.5rem;
                }

                .template-form label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #2d3748;
                }

                .template-form input,
                .template-form textarea {
                    width: 100%;
                    padding: 0.75rem 1rem;
                    border: 2px solid #dee2e6;
                    border-radius: 8px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                    background: white;
                    font-family: inherit;
                }

                .template-form textarea {
                    font-family: 'Courier New', monospace;
                    resize: vertical;
                }

                .template-form input:focus,
                .template-form textarea:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .template-form input:disabled,
                .template-form textarea:disabled {
                    background: #e9ecef;
                    cursor: not-allowed;
                }

                .template-form small {
                    display: block;
                    margin-top: 0.5rem;
                    color: #6c757d;
                    font-size: 0.875rem;
                }

                .form-group-checkbox {
                    margin-bottom: 1.5rem;
                }

                .form-group-checkbox label {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    cursor: pointer;
                    font-weight: normal;
                }

                .form-group-checkbox input[type="checkbox"] {
                    width: 1.25rem;
                    height: 1.25rem;
                    cursor: pointer;
                    accent-color: #667eea;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    padding-top: 1rem;
                    border-top: 2px solid #dee2e6;
                }

                .no-selection {
                    text-align: center;
                    padding: 4rem 0;
                    color: #adb5bd;
                }

                .no-selection i {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                }

                .no-selection p {
                    margin: 0;
                    font-size: 1.1rem;
                }

                .spinner {
                    width: 1rem;
                    height: 1rem;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }

                @media (max-width: 1024px) {
                    .templates-layout {
                        grid-template-columns: 1fr;
                    }

                    .template-list {
                        max-height: 400px;
                    }
                }
            `}</style>
        </div>
    );
};

export default EmailTemplates;
